import { ExtraScopeEnum } from '@koob/enums';
import { toDateString } from '@koob/dates';

/**
 * Change detection for the composers' "Save as draft" action (KD-3340), shared
 * by the TO and the DMC trip designers.
 *
 * Saving writes two things: the itinerary (a new trip version, server side) and
 * the trip settings the composer carries (dates, composition…). Both are
 * reduced here to a signature, once from what the API sent back for the current
 * version and once from what the composer holds — equal signatures mean the
 * save would store exactly what is already stored, so there is nothing to save.
 *
 * The itinerary signature mirrors `TripService.compareWithCurrentVersion` on the
 * API side: same fields, same normalizations, trailing product-less days
 * dropped (they are never stored). Anything the two sides cannot express
 * identically must be left out rather than guessed — a signature that differs
 * for equal content only re-enables the button, while one that matches for
 * different content would swallow a real save.
 *
 * The two composers hold their state differently (extras per day or in one flat
 * list, days carrying ids or whole products), so each passes what its own save
 * payload reads: settings are handed over already extracted, and the itinerary
 * options say which shape the days come in.
 *
 * State that has not finished loading must never read as a change — the button
 * would be live on every freshly opened trip, which is the whole point of
 * KD-3340. Hence the guards on an empty day list, on a room key still waiting
 * for its availability, and on settings the composer has no value for.
 */

// Ids reach the two sides as numbers or as strings depending on the endpoint
// they came from; compared as text so `12` and `'12'` are the same product.
const asId = (value) =>
  value === undefined || value === null ? '' : String(value);

const optionSignature = (option) =>
  [
    asId(option?.extraId),
    asId(option?.parentExtraId),
    option?.adultCount ?? '',
    normalizeBirthdates(option?.childrenBirthdates).join(','),
  ].join('~');

// Sorted so two itineraries holding the same options in a different order match.
const optionSignatures = (options = []) => options.map(optionSignature).sort();

const roomsSignature = (rooms) =>
  (rooms ?? []).map((room) =>
    [
      room?.adults ?? 0,
      room?.children ?? 0,
      normalizeBirthdates(room?.childrenBirthdates).join(','),
    ].join('~'),
  );

const daySignature = (day) =>
  JSON.stringify([
    day.dayIndex,
    asId(day.experienceId),
    asId(day.hotelId),
    day.hotelBookedOnMyOwn,
    day.hotelSelectedRoomKey ?? '',
    roomsSignature(day.roomsComposition),
    day.skipTransitionOption,
    optionSignatures(day.options),
    optionSignatures(day.globalOptions),
  ]);

// The composer holds hotel ids decoded, the trip endpoint may still send them
// as a gid — same decoding as the apps' `decodeId`, kept local so this module
// stays framework- and app-free.
const normalizeHotelId = (hotelId) => {
  if (!hotelId) {
    return '';
  }
  const asNumber = parseInt(hotelId, 10);
  if (!isNaN(asNumber)) {
    return String(asNumber);
  }
  try {
    const decoded = atob(String(hotelId).replace('\n', ''));
    return String(
      parseInt(decoded.substring(decoded.lastIndexOf('/') + 1), 10),
    );
  } catch {
    return String(hotelId);
  }
};

const normalizeParentExtraId = (parentExtraId) =>
  typeof parentExtraId === 'object'
    ? (parentExtraId?.id ?? undefined)
    : (parentExtraId ?? undefined);

// Trailing days carrying no product are not stored by the API, so they are no
// itinerary change either.
const untilLastFilledDay = (days = []) => {
  const lastFilled = days.reduce(
    (last, day, index) =>
      day?.experienceId || day?.hotelId ? index + 1 : last,
    0,
  );
  return days.slice(0, lastFilled);
};

const buildSignature = (days) =>
  JSON.stringify(
    untilLastFilledDay(days).map((day, index) =>
      daySignature({ ...day, dayIndex: index + 1 }),
    ),
  );

/**
 * Signature of the itinerary stored in the trip's current version, from the API
 * trip (`GET /trip/:id` only returns the current version's days and options).
 */
export function storedItinerarySignature(trip) {
  const globalOptionsByDayIndex = (trip?.globalOptions ?? []).reduce(
    (byDay, option) => {
      const dayIndex = option?.dayIndex;
      return {
        ...byDay,
        [dayIndex]: [...(byDay[dayIndex] ?? []), option],
      };
    },
    {},
  );

  const days = [...(trip?.tripDays ?? [])]
    .sort((a, b) => a.dayIndex - b.dayIndex)
    .map((day) => ({
      experienceId: day?.experienceId ?? undefined,
      hotelId: normalizeHotelId(day?.hotelId),
      hotelBookedOnMyOwn: day?.hotelBookedOnMyOwn ?? false,
      hotelSelectedRoomKey: day?.hotelSelectedRoomKey ?? undefined,
      roomsComposition: day?.roomsComposition ?? undefined,
      skipTransitionOption: day?.skipTransitionOption ?? false,
      options: (day?.options ?? []).map((option) => ({
        extraId: option?.optionId,
        parentExtraId: option?.parent?.option?.id ?? undefined,
        adultCount: option?.adultCount ?? undefined,
        childrenBirthdates: option?.childrenBirthdates ?? undefined,
      })),
      globalOptions: (globalOptionsByDayIndex[day?.dayIndex] ?? []).map(
        (option) => ({
          extraId: option?.optionId,
          parentExtraId: option?.parent?.option?.id ?? undefined,
          adultCount: option?.adultCount ?? undefined,
          childrenBirthdates: option?.childrenBirthdates ?? undefined,
        }),
      ),
    }));

  return buildSignature(days);
}

const comparableExtra = (extra) => ({
  extraId: extra?.id,
  parentExtraId: normalizeParentExtraId(extra?.parentExtraId),
  adultCount: extra?.adultCount ?? extra?.adults ?? undefined,
  childrenBirthdates: extra?.childrenBirthdates ?? undefined,
});

/**
 * Signature of the itinerary the composer would save, from the store days —
 * built from the very fields the save payload reads.
 *
 * @param storeDays composer days.
 * @param options.extras flat extras list carrying a 0-based `dayIndex` (DMC
 *   composer); when omitted the extras are read from each day's
 *   `selectedExtras` (TO composer).
 */
export function composedItinerarySignature(storeDays, options = {}) {
  const { extras } = options;

  const extrasOfScope = (day, dayIndex, scope) =>
    (extras
      ? extras.filter(
          (extra) => extra?.scope === scope && extra?.dayIndex === dayIndex,
        )
      : (Array.isArray(day?.selectedExtras) ? day.selectedExtras : []).filter(
          (extra) => extra?.scope === scope,
        )
    ).map(comparableExtra);

  const days = (storeDays ?? []).map((day, dayIndex) => {
    // The DMC composer still carries days holding whole products.
    const experienceId = day?.experienceId ?? day?.experience?.id;
    const hotelId = day?.hotelId ?? day?.hotel?.id;

    return {
      experienceId: experienceId ?? undefined,
      hotelId: normalizeHotelId(hotelId),
      // The API forces the flag off as soon as a hotel is picked, so the stored
      // side never carries both.
      hotelBookedOnMyOwn: hotelId ? false : (day?.hotelBookedOnMyOwn ?? false),
      // A room picked in this session sits on `selectedRoom`; one restored from
      // the draft stays on `hotelSelectedRoomKey` until its availability comes
      // back. Reading only the first would flag every freshly opened trip as
      // changed for as long as the rooms are loading.
      hotelSelectedRoomKey:
        day?.selectedRoom?.roomKey ?? day?.hotelSelectedRoomKey ?? undefined,
      roomsComposition: day?.roomsComposition ?? undefined,
      skipTransitionOption: day?.skipTransitionOption ?? false,
      options: extrasOfScope(day, dayIndex, ExtraScopeEnum.DAY),
      globalOptions: extrasOfScope(day, dayIndex, ExtraScopeEnum.GLOBAL),
    };
  });

  return buildSignature(days);
}

// The two sides carry Date objects on one side and ISO strings on the other,
// so both are cut down to the calendar day.
const normalizeDate = (date) => toDateString(date) ?? '';

const normalizeBirthdates = (birthdates = []) =>
  (birthdates ?? []).map(normalizeDate).sort();

// Themes and styles travel either as ids or as whole objects depending on where
// they were picked; both sides are reduced to a sorted list of ids.
const normalizeFilterList = (list = []) =>
  (list ?? []).map((item) => String(item?.id ?? item?.value ?? item)).sort();

const settingsNormalizers = {
  startDate: normalizeDate,
  duration: (duration) => String(duration),
  adultPax: (adultPax) => String(adultPax),
  childrenBirthdates: normalizeBirthdates,
  locale: (locale) => String(locale),
  brief: (brief) => String(brief).trim(),
  themes: normalizeFilterList,
  styles: normalizeFilterList,
};

/**
 * Whether the settings a save writes next to the itinerary differ. Each caller
 * passes the fields its own save sends, read once from the API trip and once
 * from the composer.
 *
 * A field only counts when both sides carry one. The composer rebuilds its
 * state from several sources, some arriving late; and a trip saved before a
 * field existed never carries it. Neither is the user changing something —
 * whereas a field written as empty (`''`, `[]`) is a real value and is
 * compared like any other.
 */
function tripSettingsDiffer(storedSettings = {}, composedSettings = {}) {
  const isMissing = (value) =>
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

  return Object.entries(settingsNormalizers).some(([field, normalize]) => {
    const composed = composedSettings[field];
    const stored = storedSettings[field];

    if (isMissing(composed) || stored === undefined || stored === null) {
      return false;
    }

    return (
      JSON.stringify(normalize(composed)) !== JSON.stringify(normalize(stored))
    );
  });
}

/**
 * Whether saving would change anything the API stores for this trip. A trip
 * with no client folder yet has never been saved as a draft: saving still has
 * the folder to create, so it always counts as a change.
 *
 * @param options.trip trip as the API returned it.
 * @param options.days composer days.
 * @param options.storedSettings settings read from the trip.
 * @param options.composedSettings the same settings read from the composer.
 * @param options.itinerary shape options for the composer days, see
 *   `composedItinerarySignature`.
 */
export function hasUnsavedTripChanges({
  trip,
  days,
  storedSettings,
  composedSettings,
  itinerary,
}) {
  if (!trip?.clientFolder?.id) {
    return true;
  }

  // The composer is prefilled up to the trip duration, so it never legitimately
  // holds zero days: an empty list is a composer that has not loaded yet, not an
  // itinerary someone emptied.
  const itineraryDiffers =
    days?.length > 0 &&
    storedItinerarySignature(trip) !==
      composedItinerarySignature(days, itinerary);

  return (
    itineraryDiffers || tripSettingsDiffer(storedSettings, composedSettings)
  );
}

/**
 * The slice of an API trip that `hasUnsavedTripChanges` reads, so the trip
 * designers can keep it in their persisted store (KD-3656: the Exit button
 * lives in the navbar too, outside the compose page, where the API trip is
 * not loaded) without storing the whole trip.
 */
export function tripChangeSnapshot(trip) {
  if (!trip) {
    return null;
  }

  const option = (o) => ({
    optionId: o?.optionId,
    dayIndex: o?.dayIndex,
    parent: o?.parent?.option?.id
      ? { option: { id: o.parent.option.id } }
      : undefined,
    adultCount: o?.adultCount,
    childrenBirthdates: o?.childrenBirthdates,
  });

  return {
    id: trip.id,
    clientFolder: trip.clientFolder?.id ? { id: trip.clientFolder.id } : null,
    // Not read by the check: KD-3970's navbar pill shows it outside the TD.
    currentVersion: trip.currentVersion?.id
      ? { id: trip.currentVersion.id, version: trip.currentVersion.version }
      : null,
    startDate: trip.startDate,
    duration: trip.duration,
    adultPax: trip.adultPax,
    childrenBirthdates: trip.childrenBirthdates,
    locale: trip.locale,
    tripFilters: trip.tripFilters
      ? {
          brief: trip.tripFilters.brief,
          themes: trip.tripFilters.themes,
          styles: trip.tripFilters.styles,
        }
      : undefined,
    globalOptions: (trip.globalOptions ?? []).map(option),
    tripDays: (trip.tripDays ?? []).map((day) => ({
      dayIndex: day?.dayIndex,
      experienceId: day?.experienceId,
      hotelId: day?.hotelId,
      hotelBookedOnMyOwn: day?.hotelBookedOnMyOwn,
      hotelSelectedRoomKey: day?.hotelSelectedRoomKey,
      roomsComposition: day?.roomsComposition,
      skipTransitionOption: day?.skipTransitionOption,
      options: (day?.options ?? []).map(option),
    })),
  };
}
